import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Box, Flex, Heading } from '@radix-ui/themes';
import React from 'react';
import { useIsolatedLimecraft } from '../hooks/useIsolatedLimecraft';
import { AdvancedVideoEngineProps } from '../types';
import { createPlayerObserver } from '../utils/playerObserver';
import { InteractionRect, LoadingLayer, ScrubberLayer } from './controls';

import styles from './Limecraft.module.css';

const CONTROL_BAR_HEIGHT = 50;

export const LimecraftEngine = ({
    src,
    media,
    onReady,
    tracks,
}: AdvancedVideoEngineProps) => {
    const currentTimeRef = React.useRef(0);
    const observer = React.useMemo(() => createPlayerObserver(), []);
    const aspectRatio = Math.max(media.width / media.height, 1.3666);

    const { player, iframeRef, calculatedHeight, isLoading, isError } =
        useIsolatedLimecraft(
            'lc-player-instance' as const,
            aspectRatio,
            CONTROL_BAR_HEIGHT,
        );

    React.useEffect(() => {
        if (player) {
            onReady({
                play: () => player.play(),
                pause: () => player.pause(),
                togglePlayPause: () => player.togglePlayPause(),
                faster: () => player.faster(),
                slower: () => player.slower(),
                seek: (time) => player.seek(time),
                seekRelative: (time) => player.seekRelative(time),
                seekFrames: (frames) => player.seekFrames(frames),
                seekRelativeFrames: (frames) =>
                    player.seekRelativeFrames(frames),
                nextFrame: () => player.nextFrame(),
                previousFrame: () => player.previousFrame(),
                playlistPrevious: () => player.playlistPrevious(),
                playlistNext: () => player.playlistNext(),
                osdShow: (msg, duration) => player.osdShow(msg, duration),
                osdHide: (msg) => player.osdHide(msg),
                timecodeShow: () => player.timecodeShow(),
                timecodeHide: () => player.timecodeHide(),
                timecodeToggle: () => player.timecodeToggle(),
                getCurrentTime: () => currentTimeRef.current,
                subscribe: observer.subscribe,
            });
        }
    }, [observer, onReady, player]);

    React.useEffect(() => {
        if (player && src) {
            player.setPlaylist([
                {
                    sources: [
                        {
                            src,
                            type: media.mimeType,
                            'supported-players': 'remote,local',
                        },
                    ],
                    frameRate: media.frameRate.fps,
                },
            ]);
        }
    }, [player, src, media.mimeType, media.frameRate.fps]);

    const handleSeek = React.useCallback(
        (frame: number) => {
            if (player) player.seekFrames(frame);
        },
        [player],
    );

    const zone: InteractionRect = React.useMemo(
        () => ({
            width: '100%',
            height: '20px',
            bottom: CONTROL_BAR_HEIGHT - 10,
            left: 0,
            right: 0,
            position: 'absolute',
        }),
        [],
    );

    return (
        <Box
            className={styles.engineWrapper}
            style={{
                height: calculatedHeight > 0 ? `${calculatedHeight}px` : 'auto',
            }}
        >
            <LoadingLayer isLoading={isLoading} style={{ aspectRatio }} />

            {isError && (
                <Flex
                    className={styles.mediaToggleLayer}
                    gap={'3'}
                    align={'center'}
                    justify={'center'}
                    asChild
                >
                    <Heading color='amber' weight={'bold'}>
                        <ExclamationTriangleIcon
                            height={'32px'}
                            width={'32px'}
                        />
                        Media unavailable
                    </Heading>
                </Flex>
            )}

            {!isError && (
                <iframe ref={iframeRef} className={styles.playerInstance} />
            )}

            {!isLoading && !isError && (
                <>
                    <Box
                        className={styles.mediaToggleLayer}
                        style={{ bottom: `${CONTROL_BAR_HEIGHT}px` }}
                        onClick={() => player?.togglePlayPause()}
                    />
                    <ScrubberLayer
                        totalFrames={media.frames}
                        tracks={tracks}
                        frameRate={media.frameRate}
                        onSeek={handleSeek}
                        zone={zone}
                        previewOffset={20}
                        aspectRatio={aspectRatio}
                    />
                </>
            )}
        </Box>
    );
};
